import { useCallback, useEffect, useRef, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';

export interface AdsAgentConfig {
  executionMode: 'recommend' | 'manual' | 'auto';
  minDaysBetweenActions: number;
  autoSyncMetrics: boolean;
  syncIntervalHours: number;
  targetAcos: number;
  criticalAcos: number;
  tacosMax: number;
  launchAcosMax: number;
  launchDays: number;
  learningDaysShopee: number;
  learningDaysMl: number;
  minClicksToJudge: number;
  budgetScaleStepPercent: number;
  lowStockThreshold: number;
  autoGuardrail: boolean;
  guardrailIntervalHours: number;
  autoPortfolio: boolean;
  portfolioIntervalHours: number;
  businessContext: string | null;
}

export interface AdsWindow {
  impressions: number;
  clicks: number;
  spend: number;
  directOrders: number;
  directRevenue: number;
  broadOrders: number;
  broadRevenue: number;
  totalRevenue: number;
  ctr: number;
  cvr: number;
  cpc: number;
  roas: number;
  acos: number;
  broadShare: number;
}

export interface AdsActionItem {
  tipo: string;
  acao: string;
  justificativa: string;
  impactoEsperado: string;
}

export interface AdsPendingAction {
  id: string;
  tipo: string;
  acao: string;
  justificativa: string | null;
  impactoEsperado: string | null;
  params: { budget?: number; roasTarget?: number; acosTarget?: number; status?: string };
  createdAt: string;
}

export interface AdsCampaignCard {
  campaignIdExt: string;
  connectionId: string;
  marketplace: string;
  name: string;
  adType: string | null;
  status: string | null;
  budget: number | null;
  budgetType: string | null;
  roasTarget: number | null;
  strategy: string | null;
  itemCount: number;
  syncedAt: string;
  ageDays: number;
  classification: string;
  classificationLabel: string;
  urgency: string;
  funnelStage: string | null;
  lifecycleStage: string | null;
  reasons: string[];
  metricsGranularity: 'daily' | 'window';
  metricsWindowDays: number | null;
  analysisEnabled: boolean;
  d7: AdsWindow;
  d14: AdsWindow;
  d30: AdsWindow;
  lastAnalysis: {
    id: string;
    analyzedAt: string;
    headline: string | null;
    resumo: string | null;
    actionPlan: AdsActionItem[];
    source: string | null;
  } | null;
  pendingActions: AdsPendingAction[];
}

export interface AdsAgentSummary {
  total: number;
  critical: number;
  attention: number;
  healthy: number;
  opportunity: number;
  learning: number;
  paused: number;
  no_data: number;
  spend30: number;
  revenue30: number;
  roas30: number;
  acos30: number;
}

export interface AdsAgentAlert {
  id: string;
  marketplace: string;
  campaignIdExt: string | null;
  campaignName: string | null;
  type: string;
  severity: string;
  title: string;
  message: string;
  status: string;
  createdAt: string;
}

export interface AdsOpportunity {
  listingIdExt: string;
  connectionId: string;
  marketplace: string;
  title: string | null;
  imageUrl: string | null;
  orders: number;
  revenue: number;
  conversion: number;
  visitors: number;
  ratingAverage: number | null;
  score: number;
  category: string;
}

export interface AdsRunProgress {
  status: 'running' | 'completed' | 'failed';
  type: string;
  startedAt: string;
  steps: Array<{ name: string; at: string; [k: string]: unknown }>;
  total: number;
  done: number;
  completedAt: string | null;
  error: string | null;
}

interface AdsTacos {
  days: number;
  adSpend: number;
  adRevenue: number;
  accountRevenue: number;
  tacos: number | null;
  adsShareOfRevenue: number | null;
}

function getHeaders(): Record<string, string> {
  const token = localStorage.getItem('auth_token');
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  if (token) headers.Authorization = `Bearer ${token}`;
  return headers;
}

export function useSellerAdsAgent(filters: { marketplace?: string; connectionId?: string } = {}) {
  const { user } = useAuth();
  const [summary, setSummary] = useState<AdsAgentSummary | null>(null);
  const [campaigns, setCampaigns] = useState<AdsCampaignCard[]>([]);
  const [tacos, setTacos] = useState<AdsTacos | null>(null);
  const [portfolio, setPortfolio] = useState<{ analyzedAt: string; diagnosis: Record<string, unknown> } | null>(null);
  const [alerts, setAlerts] = useState<AdsAgentAlert[]>([]);
  const [opportunities, setOpportunities] = useState<AdsOpportunity[]>([]);
  const [loading, setLoading] = useState(false);
  const [progress, setProgress] = useState<AdsRunProgress | null>(null);
  const [running, setRunning] = useState(false);

  const buildParams = useCallback(() => {
    const params = new URLSearchParams();
    if (filters.marketplace) params.set('marketplace', filters.marketplace);
    if (filters.connectionId) params.set('connectionId', filters.connectionId);
    return params.toString();
  }, [filters.marketplace, filters.connectionId]);

  const fetchOverview = useCallback(async () => {
    if (!user?.tenantId) return;
    setLoading(true);
    try {
      const [ovRes, alRes, opRes] = await Promise.all([
        fetch(`/api/seller/ads-agent/overview?${buildParams()}`, { headers: getHeaders() }),
        fetch(`/api/seller/ads-agent/alerts?${buildParams()}`, { headers: getHeaders() }),
        fetch(`/api/seller/ads-agent/opportunities?${buildParams()}`, { headers: getHeaders() }),
      ]);
      const ov = await ovRes.json();
      const al = await alRes.json();
      const op = await opRes.json();
      if (ov?.success) {
        setSummary(ov.summary || null);
        setCampaigns(ov.campaigns || []);
        setTacos(ov.tacos || null);
        setPortfolio(ov.portfolio || null);
      }
      if (al?.success) setAlerts(al.alerts || []);
      if (op?.success) setOpportunities(op.opportunities || []);
    } catch {
      // silencioso
    } finally {
      setLoading(false);
    }
  }, [user?.tenantId, buildParams]);

  useEffect(() => {
    fetchOverview();
  }, [fetchOverview]);

  // Heartbeat de progresso (mesmo padrao da Saude dos Anuncios).
  const wasRunningRef = useRef(false);
  useEffect(() => {
    if (!user?.tenantId) return;
    let stopped = false;
    let timer: number;
    const tick = async () => {
      try {
        const res = await fetch('/api/seller/ads-agent/run/status', { headers: getHeaders() });
        const json = await res.json();
        const p: AdsRunProgress | null = json?.progress || null;
        if (stopped) return;
        setProgress(p);
        const isRunning = p?.status === 'running';
        setRunning(isRunning);
        if (!isRunning && wasRunningRef.current) fetchOverview();
        wasRunningRef.current = isRunning;
      } catch {
        // ignora
      }
      if (stopped) return;
      timer = window.setTimeout(tick, wasRunningRef.current ? 3000 : 8000);
    };
    tick();
    return () => {
      stopped = true;
      window.clearTimeout(timer);
    };
  }, [user?.tenantId, fetchOverview]);

  const run = useCallback(async (opts: { type?: string; marketplace?: string; connectionId?: string; campaignIdExt?: string; days?: number } = {}) => {
    const res = await fetch('/api/seller/ads-agent/run', {
      method: 'POST',
      headers: getHeaders(),
      body: JSON.stringify({ ...opts, marketplace: opts.marketplace ?? filters.marketplace, connectionId: opts.connectionId ?? filters.connectionId }),
    });
    const json = await res.json();
    if (json?.success) {
      setProgress(json.progress);
      setRunning(true);
      wasRunningRef.current = true;
    }
    return json;
  }, [filters.marketplace, filters.connectionId]);

  const updateAlert = useCallback(async (id: string, status: 'resolved' | 'dismissed' | 'open') => {
    const res = await fetch(`/api/seller/ads-agent/alerts/${id}`, {
      method: 'PATCH',
      headers: getHeaders(),
      body: JSON.stringify({ status }),
    });
    const json = await res.json();
    if (json?.success) setAlerts((prev) => prev.filter((a) => a.id !== id));
    return json;
  }, []);

  const executeAction = useCallback(async (id: string) => {
    const res = await fetch(`/api/seller/ads-agent/actions/${id}/execute`, { method: 'POST', headers: getHeaders() });
    const json = await res.json();
    if (json?.success) fetchOverview();
    return json;
  }, [fetchOverview]);

  const dismissAction = useCallback(async (id: string) => {
    const res = await fetch(`/api/seller/ads-agent/actions/${id}/dismiss`, { method: 'POST', headers: getHeaders() });
    const json = await res.json();
    if (json?.success) fetchOverview();
    return json;
  }, [fetchOverview]);

  const fetchCampaignDetail = useCallback(async (campaignIdExt: string, connectionId?: string) => {
    const params = new URLSearchParams();
    if (connectionId) params.set('connectionId', connectionId);
    const res = await fetch(`/api/seller/ads-agent/campaigns/${encodeURIComponent(campaignIdExt)}?${params.toString()}`, { headers: getHeaders() });
    return res.json();
  }, []);

  const setAnalysisEnabled = useCallback(async (campaignIdExt: string, enabled: boolean) => {
    // otimista
    setCampaigns((prev) => prev.map((c) => (c.campaignIdExt === campaignIdExt ? { ...c, analysisEnabled: enabled } : c)));
    await fetch('/api/seller/ads-agent/analysis-prefs', {
      method: 'PATCH',
      headers: getHeaders(),
      body: JSON.stringify({ campaignIdExt, enabled }),
    }).catch(() => {});
  }, []);

  return { summary, campaigns, tacos, portfolio, alerts, opportunities, loading, progress, running, run, updateAlert, executeAction, dismissAction, fetchCampaignDetail, setAnalysisEnabled, refetch: fetchOverview };
}

// ---- Kanban da Saude de Ads --------------------------------------------------------

export interface AdsBoardCard {
  id: string;
  campaignIdExt: string;
  connectionId: string;
  marketplace: string;
  campaignName: string;
  classification: string;
  classificationLabel: string;
  column: string;
  lifecycleStage: string | null;
  urgency: string;
  headline: string | null;
  resumo: string | null;
  source: string | null;
  analysisNumber: number;
  reviewStatus: string;
  cooldownUntil: string | null;
  analyzedAt: string;
  analysisEnabled: boolean;
  metrics: { roas: number; acos: number; spend: number; ctr: number; cvr: number };
  pendingActions: AdsPendingAction[];
  pendingActionsCount: number;
}

export type AdsBoardColumns = Record<'critico' | 'atencao' | 'oportunidade' | 'aprendizado' | 'saudavel' | 'revisados', AdsBoardCard[]>;

export interface AdsAnalysisDetail {
  analysis: {
    id: string;
    campaignIdExt: string | null;
    campaignName: string | null;
    marketplace: string;
    classification: string | null;
    lifecycleStage: string | null;
    urgency: string;
    diagnosisJson: Record<string, unknown>;
    actionPlanJson: AdsActionItem[];
    metricsSnapshot: Record<string, unknown>;
    reviewStatus: string;
    reviewedBy: string | null;
    reviewedAt: string | null;
    reviewNotes: string | null;
    analyzedAt: string;
  };
  pendingActions: Array<AdsPendingAction & { status?: string }>;
  actionHistory: Array<{ id: string; tipo: string; acao: string; status: string; executedAt: string | null; executedBy: string | null; error: string | null; paramsJson: Record<string, unknown> }>;
  analysisHistory: Array<{ id: string; analyzedAt: string; classification: string | null; reviewStatus: string; diagnosisJson: Record<string, unknown> }>;
}

export function useSellerAdsBoard(filters: { marketplace?: string; connectionId?: string } = {}) {
  const { user } = useAuth();
  const [columns, setColumns] = useState<AdsBoardColumns | null>(null);
  const [total, setTotal] = useState(0);
  const [portfolio, setPortfolio] = useState<{ analyzedAt: string; diagnosis: Record<string, unknown> } | null>(null);
  const [alerts, setAlerts] = useState<AdsAgentAlert[]>([]);
  const [opportunities, setOpportunities] = useState<AdsOpportunity[]>([]);
  const [loading, setLoading] = useState(false);
  const [progress, setProgress] = useState<AdsRunProgress | null>(null);
  const [running, setRunning] = useState(false);

  const buildParams = useCallback(() => {
    const params = new URLSearchParams();
    if (filters.marketplace) params.set('marketplace', filters.marketplace);
    if (filters.connectionId) params.set('connectionId', filters.connectionId);
    return params.toString();
  }, [filters.marketplace, filters.connectionId]);

  const fetchBoard = useCallback(async () => {
    if (!user?.tenantId) return;
    setLoading(true);
    try {
      const [bdRes, alRes, opRes] = await Promise.all([
        fetch(`/api/seller/ads-agent/board?${buildParams()}`, { headers: getHeaders() }),
        fetch(`/api/seller/ads-agent/alerts?${buildParams()}`, { headers: getHeaders() }),
        fetch(`/api/seller/ads-agent/opportunities?${buildParams()}`, { headers: getHeaders() }),
      ]);
      const bd = await bdRes.json();
      const al = await alRes.json();
      const op = await opRes.json();
      if (bd?.success) {
        setColumns(bd.columns || null);
        setTotal(bd.total || 0);
        setPortfolio(bd.portfolio || null);
      }
      if (al?.success) setAlerts(al.alerts || []);
      if (op?.success) setOpportunities(op.opportunities || []);
    } catch {
      // silencioso
    } finally {
      setLoading(false);
    }
  }, [user?.tenantId, buildParams]);

  useEffect(() => {
    fetchBoard();
  }, [fetchBoard]);

  const wasRunningRef = useRef(false);
  useEffect(() => {
    if (!user?.tenantId) return;
    let stopped = false;
    let timer: number;
    const tick = async () => {
      try {
        const res = await fetch('/api/seller/ads-agent/run/status', { headers: getHeaders() });
        const json = await res.json();
        const p: AdsRunProgress | null = json?.progress || null;
        if (stopped) return;
        setProgress(p);
        const isRunning = p?.status === 'running';
        setRunning(isRunning);
        if (!isRunning && wasRunningRef.current) fetchBoard();
        wasRunningRef.current = isRunning;
      } catch {
        // ignora
      }
      if (stopped) return;
      timer = window.setTimeout(tick, wasRunningRef.current ? 3000 : 8000);
    };
    tick();
    return () => {
      stopped = true;
      window.clearTimeout(timer);
    };
  }, [user?.tenantId, fetchBoard]);

  const run = useCallback(async (opts: { type?: string; campaignIdExt?: string; days?: number } = {}) => {
    const res = await fetch('/api/seller/ads-agent/run', {
      method: 'POST',
      headers: getHeaders(),
      body: JSON.stringify({ ...opts, marketplace: filters.marketplace, connectionId: filters.connectionId }),
    });
    const json = await res.json();
    if (json?.success) {
      setProgress(json.progress);
      setRunning(true);
      wasRunningRef.current = true;
    }
    return json;
  }, [filters.marketplace, filters.connectionId]);

  const updateAlert = useCallback(async (id: string, status: 'resolved' | 'dismissed') => {
    const res = await fetch(`/api/seller/ads-agent/alerts/${id}`, { method: 'PATCH', headers: getHeaders(), body: JSON.stringify({ status }) });
    const json = await res.json();
    if (json?.success) setAlerts((prev) => prev.filter((a) => a.id !== id));
    return json;
  }, []);

  const executeAction = useCallback(async (id: string) => {
    const res = await fetch(`/api/seller/ads-agent/actions/${id}/execute`, { method: 'POST', headers: getHeaders() });
    const json = await res.json();
    if (json?.success) fetchBoard();
    return json;
  }, [fetchBoard]);

  const dismissAction = useCallback(async (id: string) => {
    const res = await fetch(`/api/seller/ads-agent/actions/${id}/dismiss`, { method: 'POST', headers: getHeaders() });
    const json = await res.json();
    if (json?.success) fetchBoard();
    return json;
  }, [fetchBoard]);

  const reviewAnalysis = useCallback(async (id: string, patch: { reviewStatus?: string; reviewNotes?: string }) => {
    const res = await fetch(`/api/seller/ads-agent/analyses/${id}/review`, { method: 'PATCH', headers: getHeaders(), body: JSON.stringify(patch) });
    const json = await res.json();
    if (json?.success) fetchBoard();
    return json;
  }, [fetchBoard]);

  const fetchAnalysisDetail = useCallback(async (id: string): Promise<AdsAnalysisDetail | null> => {
    const res = await fetch(`/api/seller/ads-agent/analyses/${encodeURIComponent(id)}`, { headers: getHeaders() });
    const json = await res.json();
    return json?.success ? json : null;
  }, []);

  const setAnalysisEnabled = useCallback(async (campaignIdExt: string, enabled: boolean) => {
    const res = await fetch('/api/seller/ads-agent/analysis-prefs', {
      method: 'PATCH',
      headers: getHeaders(),
      body: JSON.stringify({ campaignIdExt, enabled }),
    });
    const json = await res.json();
    if (json?.success) fetchBoard();
    return json;
  }, [fetchBoard]);

  return { columns, total, portfolio, alerts, opportunities, loading, progress, running, run, updateAlert, executeAction, dismissAction, reviewAnalysis, fetchAnalysisDetail, setAnalysisEnabled, refetch: fetchBoard };
}

export function useSellerAdsAgentConfig() {
  const { user } = useAuth();
  const [config, setConfig] = useState<AdsAgentConfig | null>(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const fetchConfig = useCallback(async () => {
    if (!user?.tenantId) return;
    setLoading(true);
    try {
      const res = await fetch('/api/seller/ads-agent/config', { headers: getHeaders() });
      const json = await res.json();
      if (json?.success) setConfig(json.config);
    } finally {
      setLoading(false);
    }
  }, [user?.tenantId]);

  useEffect(() => {
    fetchConfig();
  }, [fetchConfig]);

  const saveConfig = useCallback(async (patch: Partial<AdsAgentConfig>) => {
    setSaving(true);
    try {
      const res = await fetch('/api/seller/ads-agent/config', {
        method: 'PATCH',
        headers: getHeaders(),
        body: JSON.stringify(patch),
      });
      const json = await res.json();
      if (json?.success) setConfig(json.config);
      return json;
    } finally {
      setSaving(false);
    }
  }, []);

  return { config, loading, saving, saveConfig, refetch: fetchConfig };
}
